
import React from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, ArrowRight, Mail } from 'lucide-react';
import FadeIn from '../../../components/FadeIn';
import SEO from '../../../components/SEO';

const Personalization = () => {
    return (
        <div className="bg-white">
            <SEO
                title="AI-Driven Personalization Training | BPO Consultants"
                description="Scale your outreach without losing the human touch. Learn to use AI for hyper-personalized emails and video messages."
                keywords="AI Personalization, Cold Email Training, Sales Outreach Automation, Clay.run Training, Lavender AI"
            />

            {/* Hero */}
            <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-[#0B0D15] overflow-hidden">
                <div className="absolute inset-0 opacity-20">
                    <img src="https://images.unsplash.com/photo-1516321318423-f06f85e504b3?auto=format&fit=crop&q=80&w=1200" alt="Personalized message" className="w-full h-full object-cover" />
                </div>
                <div className="max-w-7xl mx-auto relative z-10 text-center">
                    <FadeIn>
                        <span className="inline-block bg-[#A2CB52]/20 text-[#A2CB52] px-4 py-2 rounded-full text-sm font-bold border border-[#A2CB52]/40 mb-6 backdrop-blur-sm">
                            <Sparkles size={14} className="inline mr-1" /> Module 3: Outreach
                        </span>
                        <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
                            AI-Driven <br /> Personalization
                        </h1>
                        <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                            The era of "Spray and Pray" is over. Learn to personalize at scale.
                        </p>
                    </FadeIn>
                </div>
            </section>

            <section className="py-20 px-4 sm:px-6 lg:px-8">
                <div className="max-w-4xl mx-auto">
                    <FadeIn className="mb-16">
                        <h2 className="text-3xl font-bold text-[#20222D] mb-6">Hyperscale Relevance</h2>
                        <p className="text-lg text-gray-600 leading-relaxed mb-8">
                            Personalization used to mean manual research for 20 minutes per lead. With diverse AI agents, we can now scrape a prospect's LinkedIn, recent news, and company report to craft a unique message in seconds.
                        </p>

                        <div className="grid md:grid-cols-2 gap-8">
                            {[
                                {
                                    title: "The 'Clay' Workflow",
                                    desc: "Mastering Clay.run to waterfall data sources and write unique hooks for every single prospect."
                                },
                                {
                                    title: "Email Grading",
                                    desc: "Using tools like Lavender to optimize subject lines, tone, and readability before you hit send."
                                },
                                {
                                    title: "AI Video Prospecting",
                                    desc: "Generating personalized video intros where you speak the prospect's name using voice synthesis."
                                },
                                {
                                    title: "LinkedIn Automation",
                                    desc: "Safe workflows for connecting and engaging on social channels without getting flagged."
                                }
                            ].map((item, i) => (
                                <div key={i} className="bg-gray-50 p-6 rounded-xl border border-gray-100">
                                    <div className="text-[#A2CB52] mb-4">
                                        <Mail size={28} />
                                    </div>
                                    <h3 className="text-xl font-bold text-[#20222D] mb-2">{item.title}</h3>
                                    <p className="text-gray-600 text-sm">{item.desc}</p>
                                </div>
                            ))}
                        </div>
                    </FadeIn>

                    <FadeIn className="mb-16 bg-gradient-to-r from-gray-900 to-gray-800 p-8 rounded-2xl text-white">
                        <h3 className="text-2xl font-bold mb-4">Case Study: The 10x ROI</h3>
                        <p className="text-gray-300 italic mb-4">
                            "Before AI, our SDRs sent 50 generic emails a day with a 2% reply rate. After this training, they send 50 hyper-personalized emails with a 12% reply rate. Same effort, 6x results."
                        </p>
                        <p className="text-[#A2CB52] font-bold">- Partner Agency CEO</p>
                    </FadeIn>

                    <FadeIn className="bg-[#20222D] rounded-3xl p-12 text-center relative overflow-hidden">
                        <div className="absolute top-0 right-0 w-64 h-64 bg-[#A2CB52] blur-[100px] opacity-20 pointer-events-none"></div>
                        <h2 className="text-3xl font-bold text-white mb-6">Upgrade Your Outreach</h2>
                        <Link to="/contact" className="inline-flex items-center bg-[#A2CB52] text-[#20222D] px-8 py-4 rounded-xl font-bold text-lg hover:bg-[#B5D575] transition-all transform hover:scale-105">
                            Start Module <ArrowRight className="ml-2" size={20} />
                        </Link>
                    </FadeIn>
                </div>
            </section>
        </div>
    );
};

export default Personalization;
